import { useCallback, useEffect, useRef, useState } from 'react';
import {
  makeStyles,
  tokens,
  Badge,
  Button,
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  DismissRegular,
  ArrowMaximizeRegular,
  ArrowMinimizeRegular,
  ChevronDownRegular,
  CopyRegular,
  CheckmarkRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { telemetryService } from '@/services/telemetryService';

export type CodePanelState = 'hidden' | 'normal' | 'maximized';

/**
 * DOM id assigned to the description text. Consumers of `<CodePanelHeader>`
 * should reference this via `aria-describedby` on the panel region so screen
 * readers announce the panel's purpose alongside its heading.
 */
export const CODE_PANEL_DESCRIPTION_ID = 'otto-code-panel-description';

type FileOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type CodePanelHeaderProps = {
  state: Exclude<CodePanelState, 'hidden'>;
  onClose: () => void;
  onToggleMaximize: () => void;
  className?: string;
};

export type CodeFileToolbarProps = {
  activeFile: string;
  fileOptions: FileOption[];
  onFileChange: (value: string) => void;
  /** Content used for Copy and Download. Must reflect the current active file. */
  fileContent: string;
  /** Parse error, if any — disables Copy/Download and shows an inline badge. */
  parseError?: string | null;
  /**
   * Read-only badge. Renders as a "Read-only" chip with `tooltip` as the
   * Fluent tooltip content, e.g. "Switch to draft mode to edit" or
   * "Contact your app administrator to request access".
   */
  readOnlyBadge?: { tooltip: string } | null;
  className?: string;
};

const COPY_FEEDBACK_MS = 1500;

const useStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    flexShrink: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    gap: tokens.spacingHorizontalS,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase400,
  },
  titleControls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
  },
  description: {
    paddingInline: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase200,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBlock: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    gap: tokens.spacingHorizontalS,
    minHeight: '36px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  fileLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  fileRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
  },
  dropdownTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingBlock: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  dropdownIcon: {
    fontSize: tokens.fontSizeBase200,
  },
  errorBadge: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  readOnlyBadge: {
    // Fluent Badge is a div by default; make it keyboard-focusable so the
    // Fluent Tooltip fires on focus as well as hover (WCAG 1.4.13, 2.1.1).
    cursor: 'help',
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  liveRegion: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
  },
});

/**
 * Panel-level header for the Code side panel. Renders the "Code" title +
 * description text and the Expand / Close controls. The file-actions toolbar
 * (dropdown + Copy + Download) lives in `<CodeFileToolbar>` so it can be
 * visually grouped with the code editor as a single object.
 *
 * The description carries `id="otto-code-panel-description"` so the surrounding
 * panel can reference it via `aria-describedby`.
 */
export const CodePanelHeader = ({
  state,
  onClose,
  onToggleMaximize,
  className,
}: CodePanelHeaderProps) => {
  const styles = useStyles();
  const isMaximized = state === 'maximized';

  return (
    <div className={mergeClasses(styles.header, className)}>
      <div className={styles.titleRow}>
        <Text as="h2" className={styles.title}>
          Code
        </Text>
        <div className={styles.titleControls}>
          <Tooltip content={isMaximized ? 'Restore' : 'Expand'} relationship="label">
            <Button
              icon={isMaximized ? <ArrowMinimizeRegular /> : <ArrowMaximizeRegular />}
              appearance="subtle"
              size="small"
              onClick={onToggleMaximize}
              aria-label={isMaximized ? 'Restore code panel' : 'Expand code panel'}
            />
          </Tooltip>
          <Tooltip content="Close" relationship="label">
            <Button
              icon={<DismissRegular />}
              appearance="subtle"
              size="small"
              onClick={onClose}
              aria-label="Close code panel"
            />
          </Tooltip>
        </div>
      </div>
      <Text id={CODE_PANEL_DESCRIPTION_ID} className={styles.description}>
        View and edit the underlying workflow definition. Changes are synced to the canvas in real time.
      </Text>
    </div>
  );
};

/**
 * File-actions toolbar rendered immediately above the code editor as part of
 * the shared "code group" card. Owns the file dropdown, Copy, Download, and
 * inline parse-error / read-only badges.
 */
export const CodeFileToolbar = ({
  activeFile,
  fileOptions,
  onFileChange,
  fileContent,
  parseError,
  readOnlyBadge,
  className,
}: CodeFileToolbarProps) => {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const copyTimerRef = useRef<number | null>(null);
  const downloadTimerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    if (downloadTimerRef.current !== null) window.clearTimeout(downloadTimerRef.current);
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
  }, []);

  // Re-announce identical messages. `role="status" aria-live="polite"` only
  // fires when the DOM text changes, so setting the same string twice in a
  // row (e.g. two copies of the same file) would be silent. Clear first, then
  // set the real message on the next paint so the DOM transitions
  // '' -> message every time.
  const announce = useCallback((message: string) => {
    setAnnouncement('');
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(() => {
      setAnnouncement(message);
      rafRef.current = null;
    });
  }, []);

  const actionsDisabled = !!parseError;

  const handleCopy = useCallback(async () => {
    if (actionsDisabled) return;
    try {
      await navigator.clipboard.writeText(fileContent);
    } catch (error) {
      // If a previous copy succeeded, ensure we clear the transient "Copied!" UI
      // state so the button feedback matches the failure.
      if (copyTimerRef.current !== null) {
        window.clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
      setCopied(false);

      announce('Copy failed');
      telemetryService.trackException(error as Error, {
        context: 'workflow.code.copy',
        file: activeFile,
      });
      telemetryService.trackEvent('workflow.code.copy', {
        file: activeFile,
        success: 'false',
      });
      return;
    }
    telemetryService.trackEvent('workflow.code.copy', {
      file: activeFile,
      success: 'true',
    });
    setCopied(true);
    announce(`${activeFile} copied to clipboard`);
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      setCopied(false);
      copyTimerRef.current = null;
    }, COPY_FEEDBACK_MS);
  }, [actionsDisabled, activeFile, announce, fileContent]);

  const handleDownload = useCallback(() => {
    if (actionsDisabled) return;
    let url: string | null = null;
    let succeeded = false;
    try {
      const blob = new Blob([fileContent], { type: 'application/json' });
      url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = activeFile;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      telemetryService.trackEvent('workflow.code.download', {
        file: activeFile,
        success: 'true',
      });
      announce(`${activeFile} downloaded`);
      succeeded = true;
    } catch (error) {
      telemetryService.trackException(error as Error, {
        context: 'workflow.code.download',
        file: activeFile,
      });
      telemetryService.trackEvent('workflow.code.download', {
        file: activeFile,
        success: 'false',
      });
      announce('Download failed');
    } finally {
      // Always release the object URL — leaks would accumulate across
      // repeated downloads even when the browser step failed midway.
      if (url !== null) URL.revokeObjectURL(url);
    }
    if (succeeded) {
      setDownloaded(true);
      if (downloadTimerRef.current !== null) window.clearTimeout(downloadTimerRef.current);
      downloadTimerRef.current = window.setTimeout(() => {
        setDownloaded(false);
        downloadTimerRef.current = null;
      }, COPY_FEEDBACK_MS);
    }
  }, [actionsDisabled, activeFile, announce, fileContent]);

  return (
    <div className={mergeClasses(styles.fileRow, className)}>
      <div className={styles.fileLeft}>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <button
              type="button"
              className={styles.dropdownTrigger}
              aria-label={`Select file, current: ${activeFile}`}
            >
              <span>{activeFile}</span>
              <ChevronDownRegular className={styles.dropdownIcon} />
            </button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {fileOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  disabled={option.disabled ?? false}
                  onClick={() => onFileChange(option.value)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
        {parseError && <Text className={styles.errorBadge}>{parseError}</Text>}
        {!parseError && readOnlyBadge && (
          <Tooltip content={readOnlyBadge.tooltip} relationship="description">
            <Badge
              appearance="ghost"
              color="informative"
              size="small"
              className={styles.readOnlyBadge}
              // Fluent Badge renders a non-focusable div by default. Making
              // it tabbable lets keyboard users trigger the Tooltip on focus
              // instead of relying on hover alone (WCAG 2.1.1 + 1.4.13).
              tabIndex={0}
              data-testid="code-panel-readonly-badge"
            >
              Read-only
            </Badge>
          </Tooltip>
        )}
      </div>
      <div className={styles.fileRight}>
        <Tooltip
          content={copied ? 'Copied!' : `Copy ${activeFile} to clipboard`}
          relationship="description"
          // Force the tooltip visible for the confirmation flash so sighted
          // users see feedback even without hovering. `undefined` on the idle
          // path returns tooltip visibility to Fluent's default hover/focus
          // behavior.
          {...(copied ? { visible: true } : {})}
        >
          <Button
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            appearance="subtle"
            size="small"
            onClick={handleCopy}
            disabled={actionsDisabled}
            aria-label={`Copy ${activeFile}`}
          >
            Copy
          </Button>
        </Tooltip>
        <Tooltip
          content={downloaded ? 'Downloaded' : `Download ${activeFile}`}
          relationship="description"
          {...(downloaded ? { visible: true } : {})}
        >
          <Button
            icon={downloaded ? <CheckmarkRegular /> : <ArrowDownloadRegular />}
            appearance="subtle"
            size="small"
            onClick={handleDownload}
            disabled={actionsDisabled}
            aria-label={`Download ${activeFile}`}
          >
            Download
          </Button>
        </Tooltip>
      </div>
      <div role="status" aria-live="polite" className={styles.liveRegion}>
        {announcement}
      </div>
    </div>
  );
};
